import React, {
    useContext, useCallback, useEffect
} from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { Tooltip } from '@jutro/components';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import PropTypes from 'prop-types';
import { MLModifierComponent } from 'ml-components-common-react';
import metadata from './AddtlInterestDriverComponent.metadata.json5';
import messages from './AddtlInterestDriverComponent.messages';
import styles from './AddtlInterestDriverComponent.module.scss';
import appConfig from 'app-config';

import { AccountService } from 'gw-capability-gateway-policy';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLHVQDefaultFieldsUtil } from 'ml-portals-util-js';

const isQnB = appConfig.portalCode === 'QB';

function RequiresProofTooltipComponent(props) {
    const translator = useTranslator();
    const { isGpa } = props;
    return (
        <Tooltip
            id="tooltipNamedNonOwner"
            placement="right"
            trigger="click"
            content={translator(messages.requiresProofTooltipContent)}
        >
            <span className={styles.tooltipOverride}>
                {translator(isGpa ? messages.gatewayRequiresProofTooltipTitle : messages.requiresProofTooltipTitle)}
                <i className="mir mi-help-outline" />
            </span>
        </Tooltip>
    );
}

function AddtlInterestDriverComponent(props) {
    const translator = useTranslator();

    const { authHeader } = useAuthentication();
    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    const viewModelService = useContext(ViewModelServiceContext);
    const {
        value: driverVM,
        index,
        policyAddress: addressVM,
        id,
        primaryDriverSpouse,
        path,
        onValueChange,
        updatePrimaryDriverSpouseEmail,
        onValidate,
        showValidationErrors,
        isGpa,
        submissionVM,
        canEditEmailFields,
        validateEmailAddress,
        isFieldDefaultedByHvq,
        updateModifiers,
        isModernProduct
    } = props;

    const {
        onValidate: setComponentValidation,
        isComponentValid,
        registerComponentValidation
    } = useValidation(id);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const validateSpouseEmailAddress = useCallback(() => {
        const primaryDriverSpouseAspects = _.get(primaryDriverSpouse[0], 'person.emailAddress_ML.aspects', undefined);
        if ((!_.isUndefined(primaryDriverSpouseAspects)) && (!primaryDriverSpouseAspects.valid || !primaryDriverSpouseAspects.subtreeValid)) {
            return false;
        }
        return true;
    }, [primaryDriverSpouse]);

    const isInCO = () => {
        return addressVM.state.value && addressVM.state.value.code === 'CO';
    };

    const isInOR = () => {
        return addressVM.state.value && addressVM.state.value.code === 'OR';
    };

    const isPrimaryDriver = useCallback(() => {
        const relationshipToInsured = _.get(driverVM, 'relationshipToInsured_ML.value.code', undefined);
        return relationshipToInsured === 'self';
    }, [driverVM]);

    const isHouseholdDriver = (primarySpouse) => {
        return primarySpouse.type_ML.value.code === 'household';
    };

    const isSpouseHouseholdDriver = () => {
        return primaryDriverSpouse
            && primaryDriverSpouse.length > 0
            && isHouseholdDriver(primaryDriverSpouse[0]);
    };

    const showSpouseEmailAddress = () => {
        return isInCO() && isPrimaryDriver() && isSpouseHouseholdDriver();
    };

    const isRatedDriver = () => {
        return driverVM.type_ML.value.code === 'rated';
    };

    const isSpouseDriver = () => {
        const relationshipToInsured = _.get(driverVM, 'relationshipToInsured_ML.value.code', undefined);
        return relationshipToInsured === 'spouse';
    };

    const primaryDriverNonRatedSpouse = () => {
        return !isRatedDriver() && isSpouseDriver();
    };

    const showNonRatedSpouseEmailAddress = () => {
        return isInCO() && isPrimaryDriver() && primaryDriverNonRatedSpouse && primaryDriverNonRatedSpouse.length > 0;
    };

    useEffect(() => {
        if (showSpouseEmailAddress()) {
            registerComponentValidation(validateSpouseEmailAddress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const drivers = _.get(driverVM, 'type_ML.baseVMNode.container.lobData.personalAuto.coverables.drivers.value');
    const isExcludedDriver = useCallback(() => {
        const excludedDriver = drivers.some((driver) => {
            return driver.type_ML === 'excluded'
        });
        return excludedDriver;
    }, [driverVM]);

    const secondaryNamedInsured = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value');
    const showDriverEmailAddress = () => {
        if (!isPrimaryDriver() && isModernProduct) {
            return false
        }
        if (isPrimaryDriver()) {
            return true;
        }
        if (isSpouseDriver() && (isInCO() || (isInOR() && isExcludedDriver()))) {
            return true;
        }
        if ((isInOR() && isExcludedDriver() && !_.isUndefined(secondaryNamedInsured))) {
            return true;
        }
        return false;
    };

    const getDriverName = () => {
        let name = _.startCase(_.toLower(driverVM.value.person.displayName));
        const suffixVal = _.get(driverVM, 'person.suffix.value.code', undefined);
        if (suffixVal) {
            const suffixTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
            const suffixName = suffixTypelist.getCode(suffixVal).name;
            name += ' ';
            name += translator({
                id: suffixName,
                defaultMessage: suffixName
            });
        }
        return name;
    };

    const requiresProofVisible = useCallback(() => {
        const requiresProofVal = _.get(driverVM, 'requiresProof_ML.value', undefined);
        if (_.isUndefined(requiresProofVal) || requiresProofVal === false) {
            return false;
        }
        return true;
    }, [driverVM]);

    const reasonRequiredVisible = useCallback(() => {
        const reasonRequiredAspect = _.get(driverVM, 'reasonRequired_Ext.aspects.required', undefined);
        if (_.isUndefined(reasonRequiredAspect) || reasonRequiredAspect === false) {
            return false;
        }
        return true;
    }, [driverVM]);


    const fetchORAvailableValues = useCallback(() => {

        const availableValues = driverVM?.reasonRequired_Ext?.aspects?.availableValues || [];

        const orCodes = [
            'driving_under_drugs',
            'other'
        ];

        const valuesToUse =
            driverVM?.requestingState_ML?.value?.code === 'OR'
                ? availableValues.filter(v => orCodes.includes(v.code))
                : availableValues;

        return valuesToUse?.map(v => ({
            code: v.code,
            name: translator({
                id: v.name,
                defaultMessage: v.name
            })
        }));
    }, [driverVM]);

    const caseNumberVisible = useCallback(() => {
        const requestingStateVal = _.get(driverVM, 'requestingState_ML.value', undefined);
        if (_.isUndefined(requestingStateVal) || _.isEmpty(requestingStateVal)) {
            return false;
        }
        return true;
    }, [driverVM]);

    const certEffDateVisible = useCallback(() => {
        const certEffDateAspect = _.get(driverVM, 'cfreffDate_ML.aspects.required', undefined);
        if (_.isUndefined(certEffDateAspect) || certEffDateAspect === false) {
            return false;
        }
        return true;
    }, [driverVM]);

    const writeValue = useCallback((value, changedPath) => {
        if (changedPath === 'person.emailAddress_ML' && isPrimaryDriver()) {
            onValueChange(value, `bindData.contactEmail`);
        } else if (changedPath === 'requiresProof_ML') {
            if (value === false) {
                driverVM.caseNumber_ML.value = null;
                driverVM.cfreffDate_ML.value = null;
                driverVM.requestingState_ML.value = null;
            }
        }

        onValueChange(value, `${path}.${changedPath}`);
    }, [driverVM.caseNumber_ML, driverVM.cfreffDate_ML, driverVM.requestingState_ML, isPrimaryDriver, onValueChange, path]);

    const disableDriverEmailField = (driver) => {
        return (isGpa && !canEditEmailFields && _.get(driver, 'value.storedEmailAddress_ML'));
    };

    const isEmailRequired = (driverVM) =>{    
        if (isQnB){
            return false
        }    
        const eSignOption = submissionVM?.bindData?.esignature_ML?.esignatureOptions?.value?.code
         const deliveryType = _.get(submissionVM,'quoteData.offeredQuotes.value[0].commPreference_ML')
        
        const isPNI = driverVM.relationshipToInsured_ML?.value?.code === 'self' 
        const emailRequired =
          (deliveryType === "electronic" &&
            ["Email", "Text"].includes(eSignOption)) ||
          (deliveryType === "paper" && eSignOption === "Email");
        return isModernProduct && isPNI && emailRequired;

    }

    const emailOnBlur = useCallback((value, changedPath) => {
        const account = _.get(driverVM, 'person');
        validateEmailAddress(account);
    }, [validateEmailAddress]);

    const emailTrim = (event) => {
        let emailAddress1 = event.target.value;
        if (emailAddress1 !== '' && emailAddress1 !== undefined) {
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1;
        }
        let currEmail = _.get(driverVM, 'person.emailAddress_ML.value');
        let isCurrEmailVerified = _.get(driverVM, 'person.emailVerified_Ext.value');
        if (currEmail !== '' && currEmail !== undefined && isCurrEmailVerified) {
            currEmail = currEmail.trim();
            _.set(driverVM, 'person.emailAddress_ML.value', currEmail)
        }
    }

    const isModifierBridgedAndDefaultedByHVQ = useCallback((model) => {
        return !!driverVM.bridgedFromHVQ_ML?.value && !!isFieldDefaultedByHvq && !!isFieldDefaultedByHvq(model);
    }, [driverVM.bridgedFromHVQ_ML, isFieldDefaultedByHvq]);

    const overrideProps = {
        driverx: {
            content: translator(messages.driverx, { driverIndex: (index + 1) })
        },
        driverName: {
            content: getDriverName()
        },
        ratedDriverEmailAddressContainer: {
            visible: showDriverEmailAddress(),
            className: isGpa ? 'vertical-spacing-20' : 'vertical-spacing-40'
        },
        driverEmailAddress: {
            disabled: disableDriverEmailField(driverVM),
            onBlur: emailOnBlur,
            onFocus: (event) => emailTrim(event),
            required: isEmailRequired(driverVM)
        },
        requiresProofContainer: {
            visible: requiresProofVisible()
        },
        requiresProofPopoverRow: {
            visible: requiresProofVisible()
        },
        reasonRequired: {
            visible: reasonRequiredVisible(),
            availableValues: fetchORAvailableValues()
        },
        caseNumber: {
            visible: caseNumberVisible()
        },
        cFREffDate: {
            visible: certEffDateVisible()
        },
        spouseEmailAddressContainer: {
            visible: isGpa && showSpouseEmailAddress(),
            onBlur: emailOnBlur,
            onFocus: (event) => emailTrim(event)
        },
        spouseEmailAddress: {
            value: primaryDriverSpouse[0] ? primaryDriverSpouse[0].person.emailAddress_ML.value : null,
            onValueChange: updatePrimaryDriverSpouseEmail,
            disabled: disableDriverEmailField(primaryDriverSpouse[0])
        },
        requiresProofInput: {
            value: _.get(driverVM, 'requiresProof_ML.value')
        },
        qnbDriverHeader: {
            visible: !isGpa
        },
        gatewayDriverHeader: {
            visible: isGpa
        },
        gatewayDriverName: {
            content: `${getDriverName()}  ${translator(messages.gatewayDriverX, { driverNumber: (index + 1) })}`
        },
        hrLine: {
            visible: !isGpa
        },
        requiresProofPopover: {
            className: !isGpa && 'ml-popover ml-popover-basic pointer-left clearfix popoverFields'
        },
        reEnterEmailContainer: {
            visible: false
        },
        nonRatedSpouseEmailAddress: {
            visible: isGpa && showNonRatedSpouseEmailAddress(),
            onBlur: emailOnBlur,
            onFocus: (event) => emailTrim(event)
        },
        driverSeparator: {
            visible: !isGpa
        },
        requiresProofTooltip: {
            isGpa
        },
        modifierComponent: {
            driverModifiers: driverVM.driverModifiers_ML,
            updateModifier: updateModifiers,
            isFieldDefaultedByHvq: isModifierBridgedAndDefaultedByHVQ,
            showValidationErrors: showValidationErrors,
            onValidate: onValidate,
            submissionVM: submissionVM,
            driverPage: false,
            visible: true
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            requiresprooftooltipcomponent: RequiresProofTooltipComponent,
            modifiercomponent: MLModifierComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            model={driverVM}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

AddtlInterestDriverComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    id: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    policyAddress: PropTypes.shape({}).isRequired,
    primaryDriverSpouse: PropTypes.shape([]).isRequired,
    path: PropTypes.string.isRequired,
    updatePrimaryDriverSpouseEmail: PropTypes.func,
    onValueChange: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool,
    updateDrivers: PropTypes.func.isRequired,
    isGpa: PropTypes.bool,
    submissionVM: PropTypes.shape({}).isRequired
};

AddtlInterestDriverComponent.defaultProps = {
    showValidationErrors: false,
    updatePrimaryDriverSpouseEmail: undefined,
    isGpa: false
};

export default AddtlInterestDriverComponent;
